import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Form, Divider, Message, Tooltip } from '@bedrock/components';
import classNames from 'classnames/bind';
import {
  useMemoizedFn,
  useDebounceFn,
  useUpdateLayoutEffect,
  useDebounceEffect,
  useWhyDidYouUpdate,
} from 'ahooks';
import { isEqual } from 'lodash';
import { HelpCircle } from '@bedrock/icons-react';
import styles from './index.less';
import SFormItem from '#components/SForm/SFormItem';
import CooperationFormList from '#features/CooperationFormList';
import PreFillQuoteButton from '#features/MPCDemand/PreFillQuoteButton';
import DescriptionsFormItem from '#components/DescriptionsFormItem';
import KPITable, { KPITableProps } from '#features/KPITable';
import AuxiliaryKpiSelect from '#features/AuxiliaryKpiSelect';
import {
  DemandKpiValueDto,
  JournalDemandDetailsResDtoCooperateAccountDto,
  enums,
  DemandCooperatePropertiesVo,
  commonGetDemandCooperateProperties2,
} from '#services/api';
import { getCooperateKpiRequired, MUST_KOL_APPROVE_ID } from '#utils/CONST';
import { $sType, localPx2rem, showAlertDebounce } from '#utils';
import { getInnerStructuredSersion } from '#features/utils';
import { isNumber } from 'lodash';
import SupplierVisible from '@mpc/common/src/features/SupplierVisible';

const cx = classNames.bind(styles);
// 策划设计拆单编辑的需要change参数
interface Dending_Design_CHANGE_OPTIONS {
  formItemKey: string | string[];
  value: any;
}

type FormValue = {
  /** 【结构化】KPI类型 */
  kpi_type?: string;
  /** 【结构化】合作内容列表 */
  cooperate_accounts: JournalDemandDetailsResDtoCooperateAccountDto[];
  /** 【结构化】【绑定执行数据】主要指标列表 */
  main_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【绑定执行数据】其他指标列表 */
  other_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】【内容营销】指标列表 */
  common_kpi_values?: DemandKpiValueDto[];
  /** 【结构化】预填报价数据 */
  prefill_quote_data?: any[];
};

type Props = {
  planInfo: any;
  handleChange?: (options: Dending_Design_CHANGE_OPTIONS) => void; // 单个值变化,主要给策划设计定制方法
  onChange?: (planInfo: any) => void; // 所有值变化
  detail: any; // 项目详情
  category_id: number; // 项目类别id
  view: boolean; // 是否是编辑类型
  hasTitle?: boolean; // 是否有标题
  kpiTableProps?: KPITableProps; // 指标表格的属性
  wrapperAttrs?: React.HTMLAttributes<HTMLDivElement>; // 最外层的属性
  syncValid?: boolean; // 是否同步校验
  showBindDataKpi?: boolean; // 是否展示绑定数据的指标
  inPlanning?: boolean; // 是否位于策划信息模块展示
  tabsModel?: boolean; // 是否是tabs模式
  splitBindExecutionData?: 0 | 1; // 拆单时是否是绑定执行数据
  showSplitFormTitle?: boolean; // 是否展示新版标题
  style?: React.CSSProperties;
  readonly?: boolean; // 是否只读,强制所有字段只读，不分场景
  updateReleaseTime?: (values: any, add?: boolean) => void; // 更新发布时间
  skipNeedAiValidation?: boolean; // 拆单场景下不校验 need_ai 行
};

const defaultPlanInfo: FormValue = {
  cooperate_accounts: [{}],
  main_kpi_values: [],
  other_kpi_values: [],
  common_kpi_values: [],
  prefill_quote_data: [],
};

type dataType =
  | 'common' // 通用模型
  | 'bindData' // 绑定执行数据，需要主要指标和其他指标，都是数易指标
  | 'contentMarketing' // 内容营销,需要通用指标
  | 'cooperateKpiRequired'; // 合作内容KPI指标必填，且没有common指标

export const getCooperateType = ({ detail, planInfo, category_id }) => {
  let type: dataType = 'common';

  // 拆单在planInfo，其他场景在detail
  if (planInfo?.is_bind_execution_data !== undefined) {
    if (['true', 1].includes(planInfo?.is_bind_execution_data)) {
      return 'bindData';
    }
  } else {
    if ([1].includes(detail?.is_bind_execution_data)) {
      return 'bindData';
    }
  }

  if (getCooperateKpiRequired(category_id)) {
    return 'cooperateKpiRequired';
  }

  // 内容营销
  if (MUST_KOL_APPROVE_ID.find((n) => category_id?.toString()?.startsWith(n))) {
    return 'contentMarketing';
  }

  return type;
};

const NewCooperate = forwardRef(function (props: Props, ref) {
  // 区分内外包
  const isInner = $sType === 'inner';
  const {
    planInfo,
    handleChange,
    onChange,
    category_id,
    detail,
    view = false,
    kpiTableProps = {},
    hasTitle = true,
    wrapperAttrs = {},
    syncValid = true,
    showBindDataKpi = true,
    inPlanning,
    tabsModel,
    style,
    splitBindExecutionData,
    showSplitFormTitle,
    updateReleaseTime,
    readonly,
    skipNeedAiValidation,
  } = props;
  const [form] = Form.useForm();
  const [errorInfo, setErrorInfo] = useState(null);

  const showPlanInfo = useMemo(() => {
    const newPlanInfo: FormValue = {
      kpi_type: planInfo?.kpi_type || detail?.kpi_type,
      cooperate_accounts: planInfo?.cooperate_accounts || [{}],
      main_kpi_values: planInfo?.main_kpi_values,
      other_kpi_values: planInfo?.other_kpi_values,
      auxiliary_kpi_values: planInfo?.auxiliary_kpi_values,
      common_kpi_values: planInfo?.common_kpi_values,
      prefill_quote_data: planInfo?.prefill_quote_data,
    };

    return newPlanInfo;
  }, [planInfo, detail]);
  const [localPlanInfo, setLocalPlanInfo] = useState(showPlanInfo);

  // 同步校验
  const { run: onlyValidate, cancel: cancelValidate } = useDebounceFn(
    () => {
      if (view || !syncValid) {
        return;
      }
      form
        .validateFields({
          validateOnly: true,
        })
        .then(() => {
          setErrorInfo(null);
        })
        .catch((errorInfo) => {
          setErrorInfo(errorInfo);
        });
    },
    {
      leading: false,
      trailing: true,
      wait: 60,
    }
  );

  const [schema, setSchema] = useState<DemandCooperatePropertiesVo | null>(
    null
  );
  useEffect(() => {
    if (!view && category_id && isInner) {
      // 跟新schema
      commonGetDemandCooperateProperties2({ category_id }).then((res) => {
        if (res?.data) {
          res.data.standards = res.data.standards?.filter(
            (si) => si.use_in_design
          );
          const necessary_rights = res.data.necessary_rights_list?.map(
            (si) => ({
              ...si,
              actual_content: si.content,
              rights_code: si.code,
            })
          );
          const expect_rights = res.data.expect_rights_list?.map((si) => ({
            ...si,
            actual_content: si.content,
            rights_code: si.code,
          }));
          setSchema({
            ...res.data,
            necessary_rights,
            expect_rights,
          });
        }
      });
    } else {
      setSchema(null);
    }
  }, [category_id, view]);

  useDebounceEffect(
    () => {
      if (isEqual(showPlanInfo, localPlanInfo)) {
        return;
      } else {
        form.setFieldsValue(showPlanInfo);
        setLocalPlanInfo(showPlanInfo);
        onlyValidate();
      }
    },
    [showPlanInfo, localPlanInfo],
    {
      wait: 50,
      leading: false,
      trailing: true,
    }
  );

  const cooperateType = useMemo(() => {
    return getCooperateType({
      detail,
      planInfo: { is_bind_execution_data: splitBindExecutionData },
      category_id,
    });
  }, [detail, splitBindExecutionData, category_id]);

  const setValueObj = useMemoizedFn((valueMap) => {
    setLocalPlanInfo((oldValue) => {
      const newPlanInfo = {
        ...oldValue,
        ...valueMap,
      };
      handleChange?.({
        formItemKey: Object.keys(valueMap),
        value: Object.values(valueMap),
      });
      form.setFieldsValue(newPlanInfo);
      return newPlanInfo;
    });
  });

  useEffect(() => {
    if (cooperateType !== 'bindData') {
      // setValueObj({
      //   main_kpi_values: [],
      //   other_kpi_values: [],
      // })
    }
    if (cooperateType !== 'contentMarketing') {
      setValueObj({
        common_kpi_values: [],
      });
    }
  }, [cooperateType]);

  const allValuesChange = (allValues) => {
    if (onChange) {
      onChange(allValues);
      setLocalPlanInfo(allValues);
    }
  };

  const { run: showErrorTip } = useDebounceFn(
    () => {
      function findErrorKey(key: string) {
        return errorInfo?.errorFields?.find((n) => n?.name?.includes(key));
      }
      if (findErrorKey('cooperate_accounts')) {
        Message.error('请补充修改合作内容属性或指标');
        return;
      }
      if (findErrorKey('common_kpi_values')) {
        Message.error('请补充通用指标');
        return;
      }
    },
    {
      leading: false,
      trailing: true,
      wait: 20,
    }
  );

  const valid = useMemoizedFn((showTip: boolean = true) => {
    form?.validateFields?.();
    showTip && showErrorTip();
    if (errorInfo) {
      showTip && showErrorTip();
    }
    return !errorInfo;
  });

  useImperativeHandle(ref, () => ({
    form,
    valid,
    asyncValid: () => {
      return form
        .validateFields()
        .then(() => {
          return true;
        })
        .catch((errorInfo) => {
          return false;
        });
    },
  }));

  useEffect(() => {
    cancelValidate?.();
    setTimeout(() => {
      onlyValidate();
    }, 1000);
  }, [category_id]);

  const updateItemKey = useRef(0);

  const resetData = () => {
    updateItemKey.current++;
    setValueObj(defaultPlanInfo);
  };

  useUpdateLayoutEffect(() => {
    resetData();
  }, [category_id]);

  const hasKPI = [
    'common',
    'contentMarketing',
    'cooperateKpiRequired',
  ].includes(cooperateType);

  useEffect(() => {
    if (!hasKPI) {
      const cooperate_accounts = form.getFieldValue('cooperate_accounts');
      const newCooperateAccounts = cooperate_accounts.map((item) => {
        return {
          ...item,
          kpi_values: [],
        };
      });
      setValueObj({
        cooperate_accounts: newCooperateAccounts,
      });
    }
  }, [hasKPI]);

  const { className: wrapperClassName, ...restWrapperAttrs } = wrapperAttrs;

  // 一级单拆单时，节点状态应该为空；一级单不存在合作内容
  const detailState = detail?.depth === 0 ? '' : detail?.state;

  // 辅助自定义指标范围的候选拉取、选项组装、提交结构组织等逻辑
  // 已全部内聚到 AuxiliaryKpiSelect 组件中，这里不再维护。

  // 订阅主要指标，用于辅助自定义指标排除已被主要指标选择的 code
  const watchedMainKpiValues =
    Form.useWatch<DemandKpiValueDto[] | undefined>('main_kpi_values', form) ??
    localPlanInfo?.main_kpi_values;
  const mainKpiSelectedCodes = useMemo(
    () =>
      (watchedMainKpiValues ?? [])
        .map((item) => item?.kpi_code)
        .filter((code): code is string => !!code),
    [watchedMainKpiValues],
  );

  // 主要指标与辅助自定义指标互斥的计算：
  // 给定本次 changedValues 和最新 allValues，若主要指标新增选中了已在
  // 辅助自定义指标里的 code，则返回需要同步写回的 auxiliary_kpi_values；
  // 否则返回 null 表示无需调整。
  // 注意：不使用 useEffect 被动订阅 + form.setFieldsValue 修正，
  //      因为那样会二次触发 onValuesChange / onChange，和外部的
  //      useDebounceEffect(showPlanInfo) 配合时会用旧 props 回写表单，
  //      导致刚勾选的主要指标被清掉。
  const computeAuxiliaryExclusion = useMemoizedFn(
    (changedValues: any, allValues: any): DemandKpiValueDto[] | null => {
      if (view) return null;
      if (!('main_kpi_values' in (changedValues ?? {}))) return null;
      const mainKpis: DemandKpiValueDto[] =
        allValues?.main_kpi_values ?? changedValues?.main_kpi_values ?? [];
      const auxiliary: DemandKpiValueDto[] =
        allValues?.auxiliary_kpi_values ?? [];
      if (!mainKpis.length || !auxiliary.length) return null;
      const mainCodeSet = new Set(
        mainKpis
          .map((item) => item?.kpi_code)
          .filter((code): code is string => !!code),
      );
      const filtered = auxiliary.filter(
        (item) => !(item?.kpi_code && mainCodeSet.has(item.kpi_code)),
      );
      return filtered.length !== auxiliary.length ? filtered : null;
    },
  );

  const updateSchemaRights = useMemoizedFn((schema) => {
    const formInfo = form.getFieldsValue();
    const cooperate_accounts = formInfo?.cooperate_accounts;
    // 防止同时拆多个单时来回切换导致重置验收标准和权益
    if (
      formInfo?.accept_desc ||
      formInfo?.cooperate_accounts?.some(
        (v) => v.necessary_rights?.length || v.expect_rights?.length
      )
    ) {
      return;
    }

    const newCooperateAccounts =
      cooperate_accounts?.map?.((item) => {
        return {
          ...item,
          necessary_rights: [],
          expect_rights: [],
        };
      }) || [];

    setValueObj({
      cooperate_accounts: newCooperateAccounts,
      accept_desc: schema?.accept_desc,
    });
  });

  const updateSchema = useMemoizedFn(() => {
    if (!schema) return;
    // 初次拆单回填模板值
    if (
      detail?.depth == 0 &&
      ['pending_design', 'underway'].includes(detail?.state) &&
      !view
    ) {
      updateSchemaRights(schema);
      return;
    }
    // 被退回后编辑，且修改了三级需求类型
    if (
      detail?.depth === 1 &&
      !view &&
      detail?.l3_category_id !== category_id &&
      detail?.l3_category_id
    ) {
      updateSchemaRights(schema);
    }
  });

  const isFirstCheckSchemaVersion = useRef(true);

  const checkSchemaVersion = useMemoizedFn(() => {
    if (!schema) return;
    const category_template_version =
      detail?.project_plan_info?.category_template_version;
    // 判断schema的版本是否和详情的版本是否相同，如果不同，则重置
    if (
      category_template_version &&
      detail.l3_category_id === category_id &&
      category_template_version !== schema?.version &&
      isFirstCheckSchemaVersion.current
    ) {
      isFirstCheckSchemaVersion.current = false;
      showAlertDebounce({
        type: 'warn',
        message: '当前模板已变更，供应商报价明细表预填写数据清空, 请重新填写',
      });

      resetData();
      setTimeout(() => {
        updateSchemaRights(schema);
      }, 10);
    } else {
      updateSchema();
    }
  });

  useEffect(() => {
    setTimeout(() => {
      checkSchemaVersion();
    }, 400);
  }, [schema, detail]);

  if (!category_id || !planInfo) {
    return null;
  }

  return (
    <div
      {...restWrapperAttrs}
      className={cx('cooperate-wrapper', wrapperClassName)}
      style={style}
    >
      <Form
        layout="vertical"
        form={form}
        scrollToFirstError={{ behavior: 'smooth' }}
        onValuesChange={(changedValues, allValues) => {
          // 主要指标变化时，同步计算需要剔除的辅助自定义指标，
          // 合并到同一次更新里，避免二次 setFieldsValue 引发的竞态
          // （父组件 onChange → 旧 props 回写 → 清掉刚选中的主要指标）。
          const nextAuxiliary = computeAuxiliaryExclusion(
            changedValues,
            allValues,
          );
          if (nextAuxiliary) {
            const mergedChanged = {
              ...changedValues,
              auxiliary_kpi_values: nextAuxiliary,
            };
            const mergedAll = {
              ...allValues,
              auxiliary_kpi_values: nextAuxiliary,
            };
            setValueObj(mergedChanged);
            allValuesChange(mergedAll);
          } else {
            setValueObj(changedValues);
            allValuesChange(allValues);
          }
          onlyValidate();
        }}
        initialValues={showPlanInfo}
        style={{
          width: '100%',
        }}
      >
        {hasTitle && (
          <>
            {!tabsModel && (
              <Divider className={cx('divider')} type="horizontal" />
            )}
            <div className={tabsModel ? cx('detail-title') : cx('title')}>
              <SupplierVisible>
                合作内容
                {tabsModel && form.getFieldValue('cooperate_accounts')?.length
                  ? `（${form.getFieldValue('cooperate_accounts')?.length}）`
                  : ''}
              </SupplierVisible>
            </div>
          </>
        )}
        {showSplitFormTitle && (
          <div
            className={cx('form-title')}
            style={{ marginTop: localPx2rem(8) }}
          >
            <SupplierVisible>合作内容</SupplierVisible>
          </div>
        )}
        <SFormItem
          label="合作内容"
          formItemKey="cooperate_accounts"
          showLabel={false}
          validateTrigger={false}
          watchChildFormValidate={true}
          required
          hideError={true}
          render={() => {
            if (!isInner) {
              // 外包
              return (
                <CooperationFormList
                  readonly={readonly}
                  key={updateItemKey.current}
                  view={true}
                  category_id={category_id}
                  hasKPI={hasKPI}
                  depth={detail.depth}
                  inPlanning={inPlanning}
                  demand_supplier_quotes={
                    detail?.demand_suppliers?.[0]?.demand_supplier_quotes
                  }
                  detail={detail}
                  schema={schema}
                  kpiTableProps={{
                    detailState: detailState,
                    detailId: detail.id,
                    structuredVersion: detail?.structured_version,
                    product_area: detail?.product_area,
                    colKeys: [
                      {
                        key: 'actual_value',
                        view: view || readonly,
                      },
                    ],
                    forOut: true,
                    ...kpiTableProps,
                  }}
                />
              );
            }
            // 内部
            return (
              <CooperationFormList
                readonly={readonly}
                key={detail?.id + category_id + updateItemKey.current}
                view={view}
                category_id={category_id}
                hasKPI={hasKPI}
                depth={detail.depth}
                inPlanning={inPlanning}
                demand_supplier_quotes={
                  detail?.demand_suppliers?.[0]?.demand_supplier_quotes
                }
                detail={detail}
                schema={schema}
                tabsModel={tabsModel}
                kpiTableProps={{
                  required:
                    !view && ['cooperateKpiRequired'].includes(cooperateType), // 只在编辑时才校验必填
                  detailId: detail.id,
                  detailState: detailState,
                  structuredVersion: getInnerStructuredSersion(detail, view),
                  forOut: false,
                  ...kpiTableProps,
                }}
                onImportSuccess={() => setTimeout(() => onlyValidate(), 1000)}
                updateReleaseTime={updateReleaseTime}
              />
            );
          }}
        />

        {['contentMarketing'].includes(cooperateType) && (
          // TODO: 不确定切换三级需求类型时，为什么common_kpi_values不会被清空，暂时只类型展示
          //   || showPlanInfo?.common_kpi_values?.length > 0
          <div
            style={{
              width: '100%',
            }}
          >
            {hasTitle && (
              <>
                {!tabsModel && (
                  <Divider className={cx('divider')} type="horizontal" />
                )}
                <div className={tabsModel ? cx('detail-title') : cx('title')}>
                  指标
                </div>
              </>
            )}
            {showSplitFormTitle && <div className={cx('form-title')}>指标</div>}
            <SFormItem
              style={{ marginTop: localPx2rem(8) }}
              label="指标"
              formItemKey="common_kpi_values"
              showLabel={false}
              validateTrigger={false}
              watchChildFormValidate={true}
              view={view}
              required={$sType === 'inner'}
              render={() => {
                if (!isInner) {
                  return (
                    <KPITable
                      key={detail?.id + category_id}
                      detailState={detailState}
                      view={true}
                      forOut={true}
                      platforms={['MPC']}
                      detailId={detail?.id}
                      structuredVersion={detail?.structured_version}
                      colKeys={[
                        {
                          key: 'actual_value',
                          view: view,
                        },
                      ]}
                      {...kpiTableProps}
                    />
                  );
                }
                return (
                  <KPITable
                    key={detail?.id + category_id}
                    detailId={detail?.id}
                    detailState={detailState}
                    structuredVersion={detail?.structured_version}
                    view={view}
                    platforms={['MPC']}
                    forOut={false}
                    {...kpiTableProps}
                  />
                );
              }}
            />
          </div>
        )}
        {showBindDataKpi &&
          ['bindData'].includes(cooperateType) &&
          detail?.structured && (
            <div>
              {hasTitle && (
                <>
                  {!tabsModel && (
                    <Divider className={cx('divider')} type="horizontal" />
                  )}
                  <div className={tabsModel ? cx('detail-title') : cx('title')}>
                    指标
                  </div>
                </>
              )}
              {showSplitFormTitle && (
                <div className={cx('form-title')}>指标</div>
              )}
              <DescriptionsFormItem
                column={2}
                items={[
                  {
                    label: '媒体达标类型',
                    formItemKey: 'kpi_type',
                    componentClass: 'select',
                    required: true,
                    view: view,
                    span: 2,
                    labelWrapper: ({ label }) => {
                      return (
                        <>
                          <span style={{ marginRight: '4px' }}>{label}</span>
                          <Tooltip
                            overlayClassName={cx(
                              'media_compliance_type_tip_overlay'
                            )}
                            overlayStyle={{ width: '400px' }}
                            title={
                              <div>
                                <div>
                                  订单达标：单笔MPC单对应的单代理单个媒体的投放数据达标；
                                </div>
                                <div>
                                  媒体达标：单个媒体有多个代理的情况下，可能有部分代理不达标，只要全部代理汇总的数据达标即为达标。比如媒体A，代理商1，2，3都不达标，但是汇总起来总和达标；
                                </div>
                                <div>
                                  整体达标：该产品全部效果广告整体达标。有多个媒体广告投放，可能有部分媒体不达标，只要全媒体汇总数据达标即为达标。
                                </div>
                              </div>
                            }
                          >
                            <HelpCircle />
                          </Tooltip>
                        </>
                      );
                    },
                    elementProps: {
                      allowClear: true,
                      options: enums.MediaKpiTypeEnum.values().filter(
                        (item) => item.value
                      ),
                    },
                  },
                  {
                    label: '主要指标',
                    formItemKey: 'main_kpi_values',
                    required: true,
                    view: view,
                    span: 2,
                    validateTrigger: false,
                    watchChildFormValidate: true,
                    render: () => {
                      return (
                        <KPITable
                          key={detail?.id}
                          detailId={detail?.id}
                          detailState={detailState}
                          colKeys={['kpi_code', 'expect_value']}
                          platforms={['DSPF']}
                          view={view}
                          // 透传 kpiTableProps（disableAdd / disableDelete 等）
                          // —— 拆单「已有执行周期」纬度下需禁止增删指标
                          {...kpiTableProps}
                        />
                      );
                    },
                  },
                  {
                    label: '其他指标',
                    formItemKey: 'other_kpi_values',
                    required: false,
                    span: 2,
                    view: view,
                    show: !view || localPlanInfo?.other_kpi_values?.length > 0,
                    validateTrigger: false,
                    watchChildFormValidate: true,
                    render: () => {
                      return (
                        <KPITable
                          key={detail?.id}
                          detailId={detail?.id}
                          detailState={detailState}
                          required={false}
                          colKeys={['kpi_code', 'expect_value']}
                          platforms={['DSPF']}
                          view={view}
                          // 透传 kpiTableProps（同上）
                          {...kpiTableProps}
                        />
                      );
                    },
                  },
                  {
                    label: '辅助自定义指标范围',
                    formItemKey: 'auxiliary_kpi_values',
                    view: view,
                    // 仅需求类型=效果买量(19) 或 A+(27) 时展示
                    // 参考：apps/mpc-inner/src/featrures/MPCDemand/Show/QuoteInformation/QuoteInfoTable.tsx
                    //      apps/mpc-inner/src/featrures/MPCDemand/Modal/BackQuoteModal.tsx
                    show: [19, 27].includes(detail?.l1_category_id),
                    // AuxiliaryKpiSelect 内部自行拉取候选并维护提交结构
                    //（DemandKpiValueDto[]，含 kpi_code + kpi_schema）
                    render: () => (
                      <AuxiliaryKpiSelect
                        detailId={detail?.id}
                        view={view}
                        excludeCodes={mainKpiSelectedCodes}
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
        {$sType === 'inner' && // 仅内包
          (inPlanning || !view) && // 策划信息模块必展示
          !(showBindDataKpi && ['bindData'].includes(cooperateType)) && // 绑定执行数据必没有
          (detail?.state === 'pending_design' ||
            detail?.depth === 0 ||
            detail.structured_version >= 1) && // 策划设计必有，结构化报价版本标识必有
          detail?.structured && (
            <>
              {!tabsModel && hasTitle && (
                <Divider className={cx('divider')} type="horizontal" />
              )}
              {!view && hasTitle && (
                <div className={cx('title')}>供应商报价明细表</div>
              )}
              {showSplitFormTitle && (
                <div className={cx('form-title')} style={{ marginBottom: 4 }}>
                  供应商报价明细表
                </div>
              )}
              <DescriptionsFormItem
                className={cx('detail-descriptions')}
                column={2}
                items={[
                  {
                    label: view ? '策划预填报价表' : '供应商报价明细表',
                    formItemKey: 'prefill_quote_data',
                    required: true,
                    view: view,
                    render: () => {
                      return (
                        <PreFillQuoteButton
                          demandId={detail?.id}
                          view={view}
                          categoryId={category_id}
                          cooperateAccountList={planInfo?.cooperate_accounts}
                          skipNeedAiValidation={skipNeedAiValidation}
                        />
                      );
                    },
                  },
                ]}
              />
            </>
          )}
      </Form>
    </div>
  );
});

export default NewCooperate;